import type { EngagementVersion } from '@clockwise/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useFetchErrorToast } from '@/hooks/use-fetch-error-toast';
import { timesheetsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface EngagementsResult {
  /** Every engagement version covering the window, across all of the target
   *  user's team customers. Filtered per entry by
   *  `resolveEngagementForEntry` — the grid never pre-groups these, because
   *  the (customer, date, activity) triple is what selects them. */
  versions: readonly EngagementVersion[];
  /** `true` once a request for the current tuple has SETTLED (success or
   *  error), or there was nothing to fetch. Until then the grid must not
   *  conclude "no engagement covers this entry" — an empty list mid-flight
   *  looks identical to a genuine zero. */
  isLoaded: boolean;
}

const EMPTY: readonly EngagementVersion[] = Object.freeze([]);

/**
 * Engagement coverage for the whole visible week, in one request.
 *
 * Mirrors `useInScopeProductsByCustomer`: the server derives the customer set
 * from the target user's memberships, so this sends only a user + window. One
 * round trip per week, cached for a minute — engagement terms don't move
 * during a data-entry session.
 */
export function useEngagements(
  userId: string | undefined,
  fromDate: string,
  toDate: string,
): EngagementsResult {
  // Same guard as the in-scope hook: an optimistic timer placeholder carries
  // an empty date until the server row swaps in, and the server 400s on a
  // blank window.
  const enabled = Boolean(userId) && Boolean(fromDate) && Boolean(toDate);
  const query = useQuery({
    queryKey: queryKeys.timesheets.engagements({ userId: userId ?? '', fromDate, toDate }),
    queryFn: () => timesheetsApi.engagements({ userId, fromDate, toDate }),
    enabled,
    staleTime: 60_000,
  });

  useFetchErrorToast(query.error, 'engagements');

  const versions = query.data?.data ?? EMPTY;
  // `isFetched`, not `isSuccess`: a failed request must not strand the grid in
  // "still loading" forever — it settles, the toast explains, and every entry
  // reads as unattributed rather than as an unanswerable question.
  const isLoaded = !enabled || query.isFetched;

  return useMemo(() => ({ versions, isLoaded }), [versions, isLoaded]);
}
